// Lib
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';

// Utils
import { getElementId } from '../../../../../common/elements/utils/elementPropertyUtils';
import { deleteCompletedTaskItems } from '../../../../../common/tiptap/extensions/taskList/taskListItemDeletionUtils';
import { getMainEditorId } from '../../../utils/elementEditorUtils';
import { keepEditorEditing } from './singleEditorSuggestionUtils';

// Hooks
import { useTiptapEditorByIdCallback } from '../../../../components/tiptapEditor/store/tiptapEditorStoreHooks';

// Actions
import { updateElement } from '../../../actions/elementActions';
import { elementUnsuggestFeature } from '../../../suggestion/suggestionActions';

// Components
import FeatureSuggestion from '../../../suggestion/FeatureSuggestion';
import { SoundId } from '../../../../sounds/soundConstants';

// Constants
import { DELETE_TASKS_SUGGESTION_DISMISSED_PROPERTY } from '../../../../../common/taskLists/taskListConstants';
import { FEATURE_SUGGESTIONS } from '../../../suggestion/suggestionConstants';

// Types
import type { ImMNElement } from '../../../../../common/elements/elementModelTypes';

type SingleEditorDeleteTasksSuggestionProps = {
    element: ImMNElement;
    question: React.ReactNode;
    isFocusedForegroundElement?: boolean;
    hideInstantly: () => void;
};

/**
 * Offers to delete the completed tasks of a single-editor task list.
 *
 * Unlike the legacy suggestion the completed tasks aren't elements that can be moved to the trash -
 * they're nodes in the editor document - so confirming deletes them from the live editor and saves
 * straight away rather than waiting out the content save debounce.
 */
const SingleEditorDeleteTasksSuggestion: React.FC<SingleEditorDeleteTasksSuggestionProps> = (props) => {
    const { element, isFocusedForegroundElement, hideInstantly } = props;

    const dispatch = useDispatch();
    const getTiptapEditorById = useTiptapEditorByIdCallback();

    const onConfirm = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();

            hideInstantly();

            const editor = getTiptapEditorById(getMainEditorId({ element, isFocusedForegroundElement }));

            if (editor) {
                deleteCompletedTaskItems(editor);
                editor.commands.flushPendingUpdate({});
            }

            dispatch(
                elementUnsuggestFeature({
                    id: getElementId(element),
                    feature: FEATURE_SUGGESTIONS.DELETE_COMPLETED_TASKS,
                }),
            );
        },
        [dispatch, element, getTiptapEditorById, hideInstantly, isFocusedForegroundElement],
    );

    // Only dismissing stops this list ever suggesting the deletion again - confirming just clears the
    // current suggestion, so a later batch of completed tasks is offered up too.
    const onDismiss = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();

            dispatch(
                updateElement({
                    id: getElementId(element),
                    changes: { [DELETE_TASKS_SUGGESTION_DISMISSED_PROPERTY]: true },
                }),
            );
        },
        [dispatch, element],
    );

    return (
        <FeatureSuggestion
            {...props}
            onMouseDown={keepEditorEditing}
            confirmSound={SoundId.DeleteSomething}
            onConfirm={onConfirm}
            onDismiss={onDismiss}
        />
    );
};

export default SingleEditorDeleteTasksSuggestion;
